import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { Helper } from '../../../core/classes/helper';
import { InputCheckConfig } from '../../../core/interfaces/input-check-config.interface';

/**
 * Class for Input Components.
 * This component is the master component for all form input components.
 * It has no template itself and should not be used directly.
 * All other input components are to be extended by it!
 *
 * @example
 * no example
 */
@Component({
	selector: 'cfe-bs-input',
	template: ''
})
export class InputComponent implements OnChanges, OnDestroy, OnInit {
	// =======================================================================================================================================
	// Inputs & Outputs
	// =======================================================================================================================================
	// Required for inputs (one of the two)
	/**
	 * Angular FormControl
	 */
	@Input() control: FormControl = new FormControl(); // Control
	/**
	 * Input ID attribute
	 */
	@Input() id: string;

	// Optional, for configuration
	/**
	 * Label
	 */
	@Input() label: string;
	/**
	 * tab Index
	 */
	@Input() tabIndex: number = null;
	/**
	 * A flag (boolean) as indication whether input field is disabled (true) or not (false)
	 */
	@Input() disabled = false;
	/**
	 * set true if you don't want to display the required marker (*)
	 */
	@Input() notMarkAsRequired = false; // A flag (boolean) to indicate whether a required input field should not be marked as such
	/**
	 * Listen for reset call
	 */
	@Input() resetObservable: Observable<boolean>;

	// =======================================================================================================================================
	// Internal variables
	// =======================================================================================================================================
	// For input fields
	markAsValid = false;
	markAsRequired = false;
	validationTestValue: any = '';
	// Subscriptions
	protected controlSubscription: Subscription;
	protected resetSubscription: Subscription;
	protected validationSubscription: Subscription;
	protected subscriptions: Subscription[] = [];

	// =======================================================================================================================================
	// Lifecycle hooks & change detections
	// =======================================================================================================================================

	/**
	 * Angular lifecyle hook onInit
	 * DO NOT REWRITE
	 */
	ngOnInit() {
		this.cfeOnInit();
	}

	/**
	 * OnInit function for overwriting to extend ngOnInit
	 */
	cfeOnInit() {}

	/**
	 * Angular Lifecycle hook OnChanges
	 * DO NOT REWRITE
	 */
	ngOnChanges(changes: SimpleChanges) {
		// Change of control (not inner value of control)
		if (changes['control']) {
			// Make sure that there is always an form control to work with
			if (!this.control) {
				this.control = new FormControl();
			}

			if (this.controlSubscription) {
				this.controlSubscription.unsubscribe();
				this.controlSubscription = null;
			}

			this.setRequiredMarker();
			this.cfeOnDisabledChange();

			if (this.control.root) {
				// Resolves Ticket SC-30518
				// When the User enters the username in the register component a async validator called uniqueUsernameValidator()
				// ist running against the api to check if the username is available. When this validator is ready it emits
				// statusChanged observable and this ngOnChanges hook is running. The setRequiredMarker() method is called and
				// the asterisk is set correctly. When the user proceed to to the second step und goes back the asterisk stays
				// orange but the Form is valid.
				// That´s the reason:
				// The register step is one big component. The different forms are displayed by ngif commands. When the user goes
				// back from the second step the form will be build again with the present value. The async validator is running
				// and this ngOnChanges will be called because this form got build again. While this ngOnChanges() method is called the status
				// of the control is "PENDING" because the validator is not ready yet so the "markAsValid" property will be set to false.
				// But the ngOnChanges() method will be called only ONCE(!) even if the async validator changes the state.
				// That´s because of a bug in the Angular Forms. When the async validator is fired once the control is build with
				// a present value the status of the control will be changed but the statusChanged observable will not be fired.
				// So this method will not be called again and the "markAsValid" property will stay on false. The control is
				// valid because the state was set by the validator. There is also no possibility to check when the async validator
				// is ready to trigger this markAdRequired() method.
				// Solution:
				// Added a finalize() method to the validator that will be fired when it´s ready. There a updateValueAndValidity()
				// method of the root form will be called to update all values and validator. When this happens the statusChanged() method
				// of the form will be changed and the setRequiredMarker() method can be called again.

				// INFO: It´s not possible to call the updateValueAndValidity() from the validator on the control it self.
				// This would cause a loop where the validator is called again and again.

				// Here is the issue on github: https://github.com/angular/angular/issues/14542
				this.control.root.statusChanges.subscribe((status) => {
					this.setRequiredMarker();
				});
			}

			// Subscribe disable status of control
			this.control.registerOnDisabledChange((isDisabled: boolean) => {
				this.cfeOnDisabledChange();
			});

			// Subscribe changes
			this.subscriptions.push(
				this.control.valueChanges.subscribe((val) => {
					this.setRequiredMarker();
				})
			);
		}

		// Change of disabled
		if (changes['disabled']) {
			changes['disabled'].currentValue ? this.control.disable() : this.control.enable();
		}

		// Change of reset observable
		if (changes['resetObservable']) {
			// Unsubscribe from old
			if (this.resetSubscription) {
				this.resetSubscription.unsubscribe();
			}

			// Subscribe new one
			if (this.resetObservable) {
				this.resetSubscription = this.resetObservable.subscribe((resetData) => {
					if (resetData) {
						this.reset(resetData);
					}
				});
			}
		}

		// Call extension function
		this.cfeOnChanges(changes);
	}

	/**
	 * OnChanges function for overwriting to extend ngOnChanges
	 *
	 * @param changes
	 */
	cfeOnChanges(changes: SimpleChanges) {}

	/**
	 * OnDisableChange function for overwriting
	 */
	cfeOnDisabledChange() {}

	/**
	 * Angular lifecycle hook OnDestroy
	 * DO NOT REWRITE
	 */
	ngOnDestroy() {
		for (const subscription of this.subscriptions) {
			subscription.unsubscribe();
		}

		if (this.controlSubscription && !this.controlSubscription.closed) {
			this.controlSubscription.unsubscribe();
		}

		if (this.resetSubscription && !this.resetSubscription.closed) {
			this.resetSubscription.unsubscribe();
		}

		// Call extension function
		this.cfeOnDestroy();
	}

	/**
	 * OnDestroy function for overwriting to extend ngOnChanges
	 */
	cfeOnDestroy() {}

	/**
	 * Empty reset function to avoid calling a undefined function
	 */
	reset(...args: any[]) {}

	// =======================================================================================================================================
	// Required & validation handling
	// =======================================================================================================================================

	/**
	 * Check if control is invalid
	 */
	public isInvalid(control: AbstractControl, config?: InputCheckConfig) {
		return Helper.isInvalid(control, config);
	}

	/**
	 * Check if control is valid
	 */
	public isValid(control: AbstractControl, config?: InputCheckConfig) {
		return Helper.isValid(control, config);
	}

	/**
	 * Check if (specific) error exists
	 */
	public inputErrors(
		control: AbstractControl,
		id?: string,
		config?: {
			only?: boolean;
			andNot?: string[];
		}
	) {
		// Set config
		config = Object.assign(
			{
				only: false,
				andNot: null
			},
			config
		);

		// No control => no error
		if (!control || !control.errors) {
			return false;
		}

		// Check errors length
		if (config.only && control.errors.length !== 1) {
			return false;
		}

		// Check if none of the specified errors included
		if (
			config.andNot &&
			config.andNot.length &&
			Object.keys(control.errors).filter((key) => config.andNot.indexOf(key) !== -1).length > 0
		) {
			return false;
		}

		// Return specific error if id is set, otherwise all errors
		return id ? control.errors[id] : control.errors;
	}

	/**
	 * Check whether the input field has required validator
	 * and if the input is valid
	 * the results are saved in the flags markAsValid & markAsRequired
	 */
	protected setRequiredMarker() {
		// Check control
		if (!this.control) {
			// Unsubscribe from previous control subscription if it existed
			if (this.validationSubscription) {
				this.validationSubscription.unsubscribe();
			}

			return;
		}
		// cloning current control to check required state
		// the original one won't be influenced
		// set value with validationTestValue and fire validation to find out
		const cloneControl = new FormControl(this.validationTestValue, this.control.validator, this.control.asyncValidator);

		// Init
		this.markAsRequired = false;

		if (!this.notMarkAsRequired) {
			// if the formcontrol includes a required validation rule
			if (cloneControl.validator) {
				const validators = cloneControl.validator(cloneControl);
				if (validators && validators.hasOwnProperty('required')) {
					this.markAsRequired = true;
				}
			}

			this.addValidationBehavior();

			// renew subscription if the control has changed
			if (this.validationSubscription) {
				this.validationSubscription.unsubscribe();
			}

			this.validationSubscription = this.control.statusChanges.subscribe(() => {
				this.addValidationBehavior();
			});
			this.subscriptions.push(this.validationSubscription);
		}
	}

	/**
	 * Set flag to the controls valid status
	 */
	protected addValidationBehavior() {
		this.markAsValid = this.control.valid || this.control.disabled;
	}
}
